// Component-specific styles
import './promotionList.scss'

// Custom Organism Components
import {CustomDataTable, FileUploadWithModal, UploadHistoryTableModal, UploadMediaModal} from '@/components/organisms'

// Custom Molecules Components
import {TabsComponent} from '@/components/molecules'

// Custom Hooks
import {usePromotionColumns} from '@/hooks/usePromotionColumns'
import {usePromotionList} from '@/hooks/usePromotionList'
import {useCountryProductTabs} from '@/hooks/useCountryProductTabs'

// Constants
import {Constants, DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {MAIN_COLUMNS, SUB_COLUMNS} from './PromotionListConstants'
import {useAppDispatch} from '@/store'

// Redux and React utilities
import {useSelector} from 'react-redux'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {useCallback, useRef} from 'react'
import { setActivePromotionTabIndex } from '@/store/slices/commonSlice'

/**
 * PromotionsList component displays a sortable, filterable, and paginated list of promotions.
 */
export const PromotionsList = () => {
  const promotionTableRef = useRef(null)
  const countryCode = useSelector(selectCountryCode)

  const {
    activeProductTabIndex: activePromotionTabIndex,
    handleProductTabChange: handlePromotionTabChange,
    productTabList: promotionTabList,
    productTabFilters: promotionTabFilters,
  } = useCountryProductTabs(countryCode, promotionTableRef)
  const {
    promotionListData,
    promotionListColConfigData,
    setSearchArry,
    isLoading,
    resetAllBtnClicked,
    formData,
    setFormData,
    downloadPromoTemplate,
    downloadPromotionFile,
    getPromotionFileUploadErrors,
    downloadUploadError,
    uploadHistoryData,
    toggleMultipleImagesModal,
    isMultipleImagesModal,
    handleCopyClick,
    handleResetAll,
    handleSorting,
    handlePageIndexChange,
    isSelectAll,
    setSelectAll,
    isModal,
    toggleModal,
    openUploadHistory,
    setOpenUploadHistory,
    setSelectedRows,
    tableActions,
  } = usePromotionList({filters: promotionTabFilters})

  // Handle reset all for tab changes
  const handleResetAllForTabChange = useCallback(() => {
    promotionTableRef?.current?.resetAll?.()
  }, [])
  const dispatch = useAppDispatch()

  // Handle promotion tab change
  const handlePromotionTabChangeWithReset = useCallback(
    index => {
      if (index === activePromotionTabIndex) {
        return
      }
      handleResetAllForTabChange()
      dispatch(setActivePromotionTabIndex(index))
      handlePromotionTabChange(index)
    },
    [activePromotionTabIndex, handleResetAllForTabChange, handlePromotionTabChange, dispatch]
  )

  const transformedColumns = usePromotionColumns(promotionListColConfigData, handleCopyClick)

  // Prepare columns for UploadHistoryTableModal
  const columns = MAIN_COLUMNS(downloadPromotionFile, downloadUploadError)

  // Promotion table component
  const promotionTable = (
    <CustomDataTable
      ref={promotionTableRef}
      columns={transformedColumns}
      records={promotionListData?.data || []}
      setSearchArry={setSearchArry}
      handleSorting={handleSorting}
      setPageIndex={handlePageIndexChange}
      pageSize={DEFAULT_TABLE_PAGE_SIZE}
      totalRecords={promotionListData?.totalRecords}
      onResetAll={() => {
        handleResetAll()
        resetAllBtnClicked()
      }}
      onRowSelect={selectedRows => setSelectedRows(selectedRows)}
      tableHeader="Promotions List"
      isLoading={isLoading}
      actions={tableActions}
      id="id"
      showActions={true}
      isSelectAll={isSelectAll}
      setSelectAll={setSelectAll}
      defaultSortConfig={{key: 'activeStartDate', direction: 'desc'}}
    />
  )

  return (
    <div className="product-admin-promotions-list">
      {promotionTabList?.length ? (
        <TabsComponent
          tabList={promotionTabList.map(item => ({
            ...item,
            tabComponent: promotionTable,
          }))}
          defaultTabIndex={activePromotionTabIndex}
          onTabChange={handlePromotionTabChangeWithReset}
        />
      ) : (
        promotionTable
      )}

      <FileUploadWithModal
        openUpload={isModal}
        setOpenUpload={toggleModal}
        setFormData={setFormData}
        formData={formData}
        title="Bulk Upload Promotions"
        showTertiaryButton={true}
        uploadFor={'product'}
        isCsvValidationRequired={false}
        isTemplateUpload={true}
        label={'Upload Bulk Promotion file'}
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
        tertiaryButtonText={'Download Template'}
        onClickTertiaryButton={downloadPromoTemplate}
        showTertiaryDownloadButton={true}
        acceptedExtensions={Constants.Xlsx}
      />
      <UploadHistoryTableModal
        openUploadHistory={openUploadHistory}
        setOpenUploadHistory={setOpenUploadHistory}
        mainData={uploadHistoryData?.data || []}
        totalRecords={uploadHistoryData?.totalRecords}
        mainColumns={columns}
        subColumns={SUB_COLUMNS}
        fetchSubRows={getPromotionFileUploadErrors}
        isDark={true}
        setPageIndex={0}
        text={
          'Access original files under File Path column, or download error reports by clicking Failed to resolve issues.'
        }
      />

      <UploadMediaModal
        openUpload={isMultipleImagesModal}
        setOpenUpload={toggleMultipleImagesModal}
        title="Upload Media"
        showTertiaryButton={true}
        formData={formData}
        setFormData={setFormData}
        entityType="promotion"
      />
    </div>
  )
}
